import React, { useState } from 'react';
import { Divider, IconButton, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Icon } from '@iconify/react';
import fileOutline from '@iconify-icons/eva/file-outline';
import emailOutline from '@iconify-icons/eva/email-outline';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { GridCallbackDetails, GridColumns } from '@mui/x-data-grid';

import { MIconButton } from '../../../../../../../@material-extend';
import MembershipDocumentsModal from './MBDocumentsModal';
import { RestoreDataGrid } from '@/RestoreComponent/DataGrid';
import {
  getGetPresignedDocumentUrlQueryKey,
  useEmailDocument,
  useGetMindBodyCustomerDocuments,
  useGetPresignedDocumentUrl
} from '@/api/documents/staff';
import { convertToStoreLocalTime } from '@/common/utils/time';
import { DocumentType } from '../index';

interface CustomerData {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobilePhone?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  photoUrl?: string;
}

interface MBDocumentsTabProps {
  customerAllData: CustomerData;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  'core-waiver': 'Core Waiver',
  'waiver-signature': 'Core Waiver Signature',
  'mb-agreement': 'MB Agreement',
  'membership-signature': 'Membership Signature',
  'medical-waiver': 'Medical Waiver',
  'legacy-medical-waiver': 'Legacy Medical Waiver'
};

const defaultSelectedVariables = {
  s3Filename: '',
  customerId: 0,
  documentType: ''
};

export default function MBDocumentsTab(props: MBDocumentsTabProps) {
  const { customerAllData } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isMBDocumentsModalOpen, setIsMBDocumentsModalOpen] =
    useState<boolean | null>(null);

  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const [emailVariables, setEmailVariables] = useState<any>(null);

  const [selectedVariables, setSelectedVariables] = useState<any>(defaultSelectedVariables);

  const { data: documentsData, isLoading: documentsLoading } =
    useGetMindBodyCustomerDocuments(
      { customerId: customerAllData?.id },
      { query: { enabled: !!customerAllData?.id } }
    );

  const tableData = documentsData?.data?.documents ?? [];

  const { data: presignedUrlData, error } = useGetPresignedDocumentUrl({
    query: {
      enabled: !!selectedVariables?.s3Filename,
      // Cast through unknown because the generated query key type expects a
      // single-element tuple with the wildcard path; we construct a two-item
      // key here and assert the type to satisfy TS.
      queryKey: ([
        '/staff/presigned-document-url',
        selectedVariables?.s3Filename
      ] as unknown) as ReturnType<typeof getGetPresignedDocumentUrlQueryKey>
    },
    // Orval can't parameterize the wildcard segment in the OpenAPI path,
    // so the codegen emits a literal `*` — override the URL here.
    request: {
      url: `/staff/presigned-document-url/${encodeURIComponent(
        String(selectedVariables?.s3Filename)
      )}`
    } as RequestInit
  });

  const presignedUrl = presignedUrlData?.data?.presignedUrl ?? '';

  React.useEffect(() => {
    if (error) {
      enqueueSnackbar('Failed to load document preview', { variant: 'error' });
    }
  }, [error]);

  const { mutate: sendEmail, isPending: sendingEmail } = useEmailDocument({
    mutation: {
      onSuccess: response => {
        if (response?.data?.success) {
          enqueueSnackbar(
            'Document copy emailed to ' +
              customerAllData?.firstName +
              ' ' +
              customerAllData?.lastName,
            {
              variant: 'success',
              action: (key: any) => (
                <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                  <CloseIcon />
                </MIconButton>
              )
            }
          );
        } else {
          enqueueSnackbar(
            'Failed to send document copy to ' +
              customerAllData?.firstName +
              ' ' +
              customerAllData?.lastName,
            { variant: 'error' }
          );
        }
      },
      onError: () => {
        enqueueSnackbar(
          'Failed to send document copy to ' +
            customerAllData?.firstName +
            ' ' +
            customerAllData?.lastName,
          { variant: 'error' }
        );
      }
    }
  });

  React.useEffect(() => {
    if (emailVariables !== null) {
      sendEmail({ data: emailVariables });
    }
  }, [emailVariables]);

  const columnConfig: GridColumns = [
    {
      field: 'type',
      headerName: 'Description',
      flex: 3,
      sortable: false,
      renderCell: ({ row }) => DOCUMENT_TYPE_LABELS[row.type] ?? row.type
    },
    {
      field: 'signedAt',
      headerName: 'Sign Date',
      flex: 2,
      sortable: false,
      renderCell: ({ row }) =>
        row.signedAt ? convertToStoreLocalTime(row.signedAt, 'YYYY-MM-DD') : ''
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-evenly'
            }}
          >
            <IconButton
              onClick={() => {
                setSelectedDocument(row);
                setIsMBDocumentsModalOpen(true);
                setSelectedVariables({
                  s3Filename: row.pdfFilename,
                  customerId: customerAllData?.id
                });
              }}
              size="small"
            >
              <Icon icon={fileOutline} color="#055577" />
            </IconButton>
            <Divider orientation="vertical" flexItem />
            {sendingEmail && emailVariables?.s3Filename === row.pdfFilename ? (
              <CircularProgress size={'2em'} />
            ) : (
              <IconButton
                disabled={sendingEmail}
                size="small"
                onClick={() => {
                  setEmailVariables({
                    s3Filename: row.pdfFilename,
                    customerId: customerAllData?.id,
                    documentType: DocumentType.LEGACY_MEMBERSHIP
                  });
                }}
              >
                <Icon icon={emailOutline} color="#055577" />
              </IconButton>
            )}
          </div>
        );
      }
    }
  ];

  const rows = tableData
    .map((row: any) => ({ ...row, id: row.guid }))
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const onPageSizeChange = (pageSize: number, _details: GridCallbackDetails) => {
    setRowsPerPage(pageSize);
    setPage(0);
  };

  return (
    <>
      <RestoreDataGrid
        rowCount={tableData.length}
        columnConfig={columnConfig}
        rows={rows}
        loading={documentsLoading}
        page={page}
        pageSize={rowsPerPage}
        pageSizes={[10, 50, 100]}
        sortModel={[]}
        emptyListMessage={`No documents found for ${customerAllData?.firstName} ${customerAllData?.lastName}`}
        onPageChange={setPage}
        onPageChangeSize={onPageSizeChange}
        onSortModelChange={() => {}}
      />
      {selectedDocument && (
        <MembershipDocumentsModal
          presignedURL={presignedUrl}
          showEmailButton={true}
          selectedDocument={selectedDocument}
          open={isMBDocumentsModalOpen}
          setModal={(close: boolean) => {
            setSelectedVariables(defaultSelectedVariables);
            setIsMBDocumentsModalOpen(close)
          }}
          setEmailVariables={setEmailVariables}
          selectedVariables={selectedVariables}
          sendingEmail={sendingEmail}
          showPrintButton={false}
        />
      )}
    </>
  );
}
